import type { PlannedTargetWeight } from "./schemas/plan.js";

// Per-set weight provenance. Persisted on LogSet.weightSource so the
// post-session "did the athlete deviate from prescription" view, the
// percent-of-MAX delta math, and a future "re-prefill on resume" path
// all read a stable label. Data-only — no UI cue is rendered, by design
// (see next/archive/default-weights.md). 'blank' is a transient signal from
// resolveDefaultWeight meaning "no prefill" and is never persisted.
export type WeightSource =
  | "prescribed-absolute"
  | "computed-percent"
  | "history"
  | "manual";

// A weight the CHAIN filled in (prescription / %MAX / history), as opposed
// to one the athlete typed ('manual') or touched-without-provenance
// (absent — equivalent to manual by LogSet convention). The save path omits
// non-prefilled sources; the swap path clears a prefilled weight because it
// described the OLD exercise's prescription (IB-8 follow-up: bench 40 kg
// carried onto a dumbbell press would read as 40 per implement = 80 total).
export const isPrefilledWeightSource = (
  source: WeightSource | undefined,
): source is Exclude<WeightSource, "manual"> =>
  source !== undefined && source !== "manual";

export type ResolvedDefaultWeight = {
  kg: number;
  source: WeightSource | "blank";
};

// Single resolution chain used by buildPlanStarter (server-author and
// client-author flows alike) so mobile + web + post-session math agree
// on what number prefilled a set and why. Pure: bodyweight subtraction
// (extra = total − bw) is the caller's job since it depends on the
// catalog row, not on the intent.
export const resolveDefaultWeight = (
  intent: PlannedTargetWeight,
  ctx: {
    /** Athlete's current MAX kg for the exercise (canonical kg). */
    userMax: number | undefined;
    /** Last logged weight on this exercise (canonical kg). Empty when
     *  the athlete has no history for it yet. */
    lastLoggedKg: number | undefined;
  },
): ResolvedDefaultWeight => {
  if (intent.kind === "absolute") {
    return { kg: intent.weightKg, source: "prescribed-absolute" };
  }
  if (intent.kind === "percentage") {
    if (ctx.userMax !== undefined) {
      const kg = Math.round((ctx.userMax * intent.pctOfMax) / 100 * 100) / 100;
      return { kg, source: "computed-percent" };
    }
    // A percent-of-MAX prescription with no known MAX must NOT silently
    // borrow the athlete's last logged weight — that shows a number the
    // coach never prescribed (Antenore 2026-06-01: a gym-network blip
    // emptied the MAX map and benches prefilled off history). Leave the
    // cell blank so it reads as "prescription pending a MAX", not a fake
    // target. The % intent is still threaded for a later re-prefill once
    // the MAX is known. (Was: fall back to lastLoggedKg → "history".)
    return { kg: 0, source: "blank" };
  }
  // kind === "history" OR kind === "none"
  if (ctx.lastLoggedKg !== undefined) {
    return { kg: ctx.lastLoggedKg, source: "history" };
  }
  return { kg: 0, source: "blank" };
};

// EQ-1-SWAP-PCTMAX — carry a weight from one exercise to another as the same
// FRACTION of the athlete's MAX. Antenore, 2026-08-20: *"quando l'esercizio ha
// un peso fisso, in teoria, il sistema potrebbe calcolare quanto è in
// percentuale del MAX e convertire il peso dell'attrezzo alternativo di
// conseguenza se un MAX è registrato."*
//
// It handles his two cases with one rule. A percent-of-MAX prescription
// converts exactly (the fraction is the prescription). A fixed prescribed
// weight converts by the fraction it implicitly represents. Neither needs the
// plan intent, which is what makes this reachable at all: `buildPlanStarter`
// resolves the intent to kg and does NOT carry it onto PlanStarter, so the
// resolved kg is the only thing a mid-session swap still has in hand.
//
// ⛔ The TRUST of the two MAXes is the caller's gate (isMaxTrustedForProposal),
// not this function's: this one is arithmetic, and folding a policy into it
// would hide the refusal in a place nobody reviewing "the conversion" would
// look. ⛔ Bodyweight exercises are out of scope for the same reason they are
// special in buildPlanStarter — a MAX there is TOTAL load and the logger shows
// the EXTRA, so the ratio would silently compare two different quantities.
// The caller refuses those too.
//
// ⛔ Rounding is NOT done here. A ratio produces un-loadable numbers (68.38 kg)
// and the floor depends on the athlete's plate inventory, which lives in the
// UI layer; `roundLoadable` is the one place that knows how. Returning the raw
// kg keeps this pure and keeps one rounding implementation in the codebase.
//
// Returns null when the conversion cannot be made, which the caller reads as
// "leave the cell blank" — the same answer resolveDefaultWeight gives a
// percent with no MAX, and for the same reason: an invented number is worse
// than an empty cell.
export const convertWeightByMaxRatio = (params: {
  fromWeightKg: number | undefined;
  fromMaxKg: number | undefined;
  toMaxKg: number | undefined;
}): number | null => {
  const { fromWeightKg, fromMaxKg, toMaxKg } = params;
  if (
    fromWeightKg === undefined ||
    fromMaxKg === undefined ||
    toMaxKg === undefined
  ) {
    return null;
  }
  if (
    !Number.isFinite(fromWeightKg) ||
    !Number.isFinite(fromMaxKg) ||
    !Number.isFinite(toMaxKg)
  ) {
    return null;
  }
  // A non-positive MAX is not a small MAX, it is a missing one: `cheese-
  // sandwich` really does sit at 0 kg in production, and dividing by it would
  // hand back Infinity dressed as a training weight.
  if (fromWeightKg <= 0 || fromMaxKg <= 0 || toMaxKg <= 0) return null;
  return (fromWeightKg / fromMaxKg) * toMaxKg;
};
